import React, { useState } from 'react'
import ProcessingModal from './ProcessingModal'
import { CameraSwitchButton } from './CameraSwitchButton'
import { FaLock, FaLockOpen, FaTimes, FaMoneyBillWave, FaExpand, FaCompress } from 'react-icons/fa'
import { FaGear, FaHardDrive, FaPrint } from 'react-icons/fa6'
import ConfirmModal from './ConfirmModal'
import { useSessionReset } from '@renderer/stores/sessionState'
import PinModal from './PinModal'
import { ReprintModal } from './view'
import DiskCleanupModal, { useDiskCleanupStatus } from './view/DiskCleanupModal'
import { useFullscreenManager, useResponsiveTier } from '@renderer/hooks'
import {
  capabilities,
  getDiskCleanupApi,
  hasReprintSessionApi,
  isDigitalMode
} from '@renderer/lib/runtime'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useConfigStore } from '@renderer/stores/configState'
import cogoToast from '@successtar/cogo-toast'
import { useTranslation } from 'react-i18next'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { bkSkipPaymentTarget, markBkPaymentSkip } from '@renderer/pages/bingkai-kenangan/flow'
import { exitNeedsPin } from '@renderer/utils/exitPinGate'

interface ControlButtonsProps {
  isRootPath: boolean
  setShowSettings: (show: boolean, fromGear?: boolean) => void
  hideExitButton?: boolean
}

const ControlButtons: React.FC<ControlButtonsProps> = ({
  isRootPath,
  setShowSettings,
  hideExitButton = false
}) => {
  const { t } = useTranslation()
  const [showExitModal, setShowExitModal] = useState(false)
  const [showExitPinModal, setShowExitPinModal] = useState(false)
  const [showSkipPaymentModal, setShowSkipPaymentModal] = useState(false)
  const [showReprintPinModal, setShowReprintPinModal] = useState(false) // State for Reprint PIN check
  const [showReprintModal, setShowReprintModal] = useState(false) // State for Reprint Modal
  const [showDiskPinModal, setShowDiskPinModal] = useState(false)
  const [showDiskCleanup, setShowDiskCleanup] = useState(false)
  const [isQuitting, setIsQuitting] = useState(false)
  const sessionReset = useSessionReset()
  const { primaryColor } = useConfigStore.getState().config?.theme || {
    primaryColor: '#3B82F6'
  }

  const [isReprintPaymentActive, setIsReprintPaymentActive] = useState(false)
  const [showReprintBypassPinModal, setShowReprintBypassPinModal] = useState(false)
  const [isAiEffectPaymentActive, setIsAiEffectPaymentActive] = useState(false)
  const [showAiBypassPinModal, setShowAiBypassPinModal] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const isPaymentPage = location.pathname.endsWith('/payment')
  const isCheckoutPage = location.pathname.endsWith('/checkout')
  const isResultPage =
    location.pathname.endsWith('/result') || location.pathname.endsWith('/post-session-ai')
  // Reprint button visible on Welcome (isRootPath) or Result page
  const showReprintButton = isRootPath || isResultPage

  const { password } = useSettingsStore()
  const { getPageSettings } = useConfigStore()
  const welcomeSettings = getPageSettings('welcome')
  const {
    isFullscreen,
    isStartupGracePeriodActive,
    showSubmitPinModal,
    setShowSubmitPinModal,
    handleFullscreenToggle,
    handlePinSubmit
  } = useFullscreenManager()
  const { isCompact: tierCompact } = useResponsiveTier()
  const compactChrome = tierCompact || capabilities.isMobile
  /** Desktop / kiosk: slightly larger icons + hit targets (e.g. 1440×900) */
  const iconClass = compactChrome ? 'text-[1.25rem]' : 'text-[1.75rem]'
  const hitArea =
    'inline-flex items-center justify-center touch-manipulation ' +
    (compactChrome ? 'min-h-9 min-w-9' : 'min-h-11 min-w-11')

  /** Fullscreen + PIN: faint during normal use, clearer during startup grace; avoid opacity-10 (invisible on light UIs). */
  const fullscreenPinIdleOpacity =
    isFullscreen && password && !isStartupGracePeriodActive
  const fullscreenPinGraceOpacity = isFullscreen && password && isStartupGracePeriodActive

  React.useEffect(() => {
    const handleReprintPaymentState = (event: Event): void => {
      const customEvent = event as CustomEvent<{ visible: boolean }>
      setIsReprintPaymentActive(customEvent.detail.visible)
    }

    window.addEventListener('reprint-payment-state', handleReprintPaymentState)
    return (): void => {
      window.removeEventListener('reprint-payment-state', handleReprintPaymentState)
    }
  }, [])

  React.useEffect(() => {
    const handleAiEffectPaymentState = (event: Event): void => {
      const customEvent = event as CustomEvent<{ visible: boolean }>
      setIsAiEffectPaymentActive(customEvent.detail.visible)
    }

    window.addEventListener('ai-effect-payment-state', handleAiEffectPaymentState)
    return (): void => {
      window.removeEventListener('ai-effect-payment-state', handleAiEffectPaymentState)
    }
  }, [])

  const hasReprintAPI = hasReprintSessionApi()
  // Mode-driven chrome gates. Each is a small read so React re-runs them
  // on the (rare) host change — host is effectively static once the page
  // is loaded.
  const digital = isDigitalMode()
  // In digital mode there is no paid reprint flow, no operator settings,
  // and no payment-skip affordance — consumers don't use any of these.
  const showReprint = !digital
  const showSettings = !digital
  const showSkipPayment = !digital
  // The fullscreen control renders as a padlock (FaLock) and is tied to the
  // kiosk PIN-lock concept — meaningless and confusing for a consumer web
  // booth. Hide it entirely in digital; a consumer can use the browser's own
  // fullscreen if they want it.
  const showFullscreenToggle = !digital
  // Low-disk warning: Welcome only, so it never shows mid-session, and only on
  // shells that carry the cleanup IPC.
  const diskCleanupEnabled = !digital && isRootPath && !!getDiskCleanupApi()
  const { status: diskStatus, refresh: refreshDiskStatus } = useDiskCleanupStatus(diskCleanupEnabled)
  const diskLevel = diskCleanupEnabled ? diskStatus?.level : undefined

  const handleOpenSettings = (): void => {
    if (isFullscreen && password && !isStartupGracePeriodActive) {
      // If in fullscreen and PIN is set, require PIN to open settings
      setShowSettings(false)
      setShowSubmitPinModal(true)
    } else {
      // If not in fullscreen, or in fullscreen but no PIN set (new user), or during grace period, allow opening settings
      setShowSettings(true, true)
    }
  }

  const handleOpenDiskCleanup = (): void => {
    if (isFullscreen && password && !isStartupGracePeriodActive) setShowDiskPinModal(true)
    else setShowDiskCleanup(true)
  }

  const handleOpenReprint = (): void => {
    if (isFullscreen && password && !isStartupGracePeriodActive) {
      // If in fullscreen and PIN is set, require PIN to open reprint
      setShowReprintPinModal(true)
    } else {
      // If not in fullscreen, or in fullscreen but no PIN set, or during grace period, allow opening reprint
      setShowReprintModal(true)
    }
  }

  const handleSkipPayment = (pin: string): void => {
    if (pin === password) {
      // Check if it's booth-daughter or newspaper custom path
      const customPath = welcomeSettings?.customPath
      const { getPageSettings } = useConfigStore.getState()
      const filterSettings = getPageSettings('filter')
      const applyAfterCapture = filterSettings?.applyAfterCapture || false

      let nextPage: string


      if (customPath === 'booth-daughter') {
        nextPage = 'capture'
      } else if (customPath === 'bingkai-kenangan') {
        // BK's own order (cut and payment either way round, per "Start with
        // payment"), never the stock targets below: the cut sets the shot count.
        const pages = useConfigStore.getState().config?.pages
        const target = bkSkipPaymentTarget(pages, isPaymentPage ? 'payment' : 'checkout')
        // A skipped session still gets its one sheet: copies is only set when
        // the payment QR is minted, and BK's print step prints nothing at 0.
        const session = useSessionStateStore.getState()
        if ((session.userPreferences?.copies ?? 0) < 1) {
          session.setSessionState({ userPreferences: { copies: 1 } })
        }
        if (!target) {
          // On the cut screen, before a cut: stay, and step over payment once
          // the guest picks one.
          markBkPaymentSkip()
          useSessionStateStore.getState().logActivity('Payment skipped by operator', { to: 'after cut' })
          cogoToast.success(t('controls.bkSkipAfterCut', 'Payment skipped. Choose a cut to continue.'))
          setShowSkipPaymentModal(false)
          return
        }
        nextPage = target
      } else if (welcomeSettings?.startWithPayment) {
        // When startWithPayment is true, check applyAfterCapture
        // If applyAfterCapture is true, filter comes AFTER capture, so skip filter → go to frame
        // If applyAfterCapture is false, filter comes BEFORE capture, so go to filter
        nextPage = applyAfterCapture ? 'frame' : 'filter'
      } else {
        nextPage = 'capture'
      }

      useSessionStateStore.getState().logActivity('Payment skipped by operator', { to: nextPage })
      navigate(`/${projectId}/${nextPage}`)
      setShowSkipPaymentModal(false)
    } else {
      cogoToast.error(t('controls.wrongPin', 'Oops! Wrong PIN. Please try again.'))
    }
  }

  // A paid session takes the PIN to end (see exitNeedsPin); an unpaid one keeps
  // the yes/no confirm. Read at tap time so a settlement that just landed counts.
  const handleExitClick = (): void => {
    if (exitNeedsPin(useSessionStateStore.getState().transaction?.status, password)) {
      setShowExitPinModal(true)
    } else {
      setShowExitModal(true)
    }
  }

  const endSessionAsOperator = async (): Promise<void> => {
    setIsQuitting(true)
    try {
      await sessionReset('operator_exit')
    } catch (error) {
      console.error('[CONTROLS] Error during session reset:', error)
    } finally {
      setIsQuitting(false)
    }
  }

  const handleExitPin = (pin: string): void => {
    if (pin === password) {
      setShowExitPinModal(false)
      void endSessionAsOperator()
    } else {
      useSessionStateStore.getState().logActivity('Exit session: wrong PIN on a paid session')
      cogoToast.error(t('controls.wrongPin', 'Oops! Wrong PIN. Please try again.'))
    }
  }

  const handleReprintBypass = (pin: string): void => {
    if (pin === password) {
      // Dispatch event to UserReprintModal
      const event = new CustomEvent('user-reprint-bypass')
      window.dispatchEvent(event)
      setShowReprintBypassPinModal(false)
    } else {
      cogoToast.error(t('controls.wrongPin', 'Oops! Wrong PIN. Please try again.'))
    }
  }

  const handleAiEffectBypass = (pin: string): void => {
    if (pin === password) {
      // Dispatch event to PostSessionAiPaymentModal → settles as 'bypass' and runs generation.
      window.dispatchEvent(new CustomEvent('ai-effect-bypass'))
      setShowAiBypassPinModal(false)
    } else {
      cogoToast.error(t('controls.wrongPin', 'Oops! Wrong PIN. Please try again.'))
    }
  }

  return (
    <>
      {/* Top chrome lives outside Welcome's ScaleToFit; no canvas counter-scale needed here.
          Rendered on every session page (incl. post-session AI) so the exit / fullscreen-lock /
          skip-payment affordances are always reachable. Each button self-gates below. */}
      <div
        className="pointer-events-none absolute top-0 right-0 left-0 z-[100] flex justify-end items-start"
        style={
          capabilities.safeAreaInsets ? { paddingTop: 'env(safe-area-inset-top)' } : undefined
        }
      >
        <div className="pointer-events-auto relative flex h-auto items-center py-0.5">
          <div
            className={`flex h-full items-center rounded-bl-md ${compactChrome ? 'gap-0 px-2 py-1' : 'gap-1 px-3 py-1.5'}`}
          >
            {(diskLevel === 'low' || diskLevel === 'critical') && (
              <button
                type="button"
                title={t('diskCleanup.lowTitle', 'Disk almost full')}
                onClick={handleOpenDiskCleanup}
                className={`transition-colors ${hitArea} ${
                  diskLevel === 'critical' ? 'text-red-600 animate-pulse' : 'text-amber-500'
                }`}
              >
                <FaHardDrive className={iconClass} />
              </button>
            )}
            {showReprint && showReprintButton && hasReprintAPI && (isRootPath || (isResultPage && !isFullscreen)) && (
              <button
                type="button"
                title={t('reprint.title', 'Reprint')}
                onClick={handleOpenReprint}
                className={`text-carbon/75 hover:text-carbon transition-colors ${hitArea}`}
              >
                <FaPrint
                  className={`${iconClass} ${
                    fullscreenPinGraceOpacity
                      ? 'opacity-80 animate-pulse'
                      : fullscreenPinIdleOpacity
                        ? 'opacity-55'
                        : ''
                  }`}
                />
              </button>
            )}

            {showSettings && isRootPath && (!isFullscreen || !password) && (
              <button
                type="button"
                title={t('controls.settings', 'Settings')}
                onClick={handleOpenSettings}
                className={`text-carbon/75 hover:text-carbon transition-colors ${hitArea}`}
              >
                <FaGear className={iconClass} />
              </button>
            )}
            {/* Digital + mobile only — self-gates internally; renders nothing on
                kiosk / desktop. Sits right next to the fullscreen control so the
                consumer can flip front/back camera at a glance. */}
            <CameraSwitchButton className={`text-carbon/75 hover:text-carbon transition-colors ${hitArea}`} iconClassName={iconClass} />
            {/* Digital: a plain fullscreen control (expand/compress). The
                kiosk padlock below stays kiosk-only — its PIN-lock semantics
                would confuse consumers, but a booth with NO controls at all
                reads as broken on laptops. */}
            {digital && (
              <button
                type="button"
                title={
                  isFullscreen
                    ? t('controls.exitFullscreen', 'Exit Fullscreen')
                    : t('controls.enterFullscreen', 'Enter Fullscreen')
                }
                onClick={handleFullscreenToggle}
                className={`text-carbon/75 hover:text-carbon transition-colors ${hitArea}`}
              >
                {isFullscreen ? (
                  <FaCompress className={iconClass} />
                ) : (
                  <FaExpand className={`${iconClass} opacity-75`} />
                )}
              </button>
            )}
            {showFullscreenToggle && <button
              type="button"
              title={
                isFullscreen
                  ? t('controls.exitFullscreen', 'Exit Fullscreen')
                  : t('controls.enterFullscreen', 'Enter Fullscreen')
              }
              onClick={handleFullscreenToggle}
              className={`text-carbon/75 hover:text-carbon transition-colors ${hitArea}`}
            >
              {isFullscreen ? (
                <FaLock
                  className={`${iconClass} ${
                    password
                      ? fullscreenPinGraceOpacity
                        ? 'opacity-85 animate-pulse'
                        : fullscreenPinIdleOpacity
                          ? 'opacity-55'
                          : ''
                      : isStartupGracePeriodActive
                        ? 'opacity-75 animate-pulse'
                        : 'opacity-55'
                  }`}
                />
              ) : (
                <FaLockOpen className={`${iconClass} animate-pulse`} />
              )}
            </button>}
            {showSkipPayment && isReprintPaymentActive && (
              <button
                type="button"
                title={t('controls.skipPayment', 'Skip Payment')}
                onClick={() => setShowReprintBypassPinModal(true)}
                className={`text-carbon/75 hover:text-carbon transition-colors ${hitArea}`}
                id="reprint-bypass-btn"
              >
                <FaMoneyBillWave className={`${iconClass} opacity-55`} />
              </button>
            )}
            {/* Operator skip-payment for the AI effect charge (mirrors reprint bypass). */}
            {isAiEffectPaymentActive && (
              <button
                type="button"
                title={t('controls.skipPayment', 'Skip Payment')}
                onClick={() => setShowAiBypassPinModal(true)}
                className={`text-carbon/75 hover:text-carbon transition-colors ${hitArea}`}
                id="ai-effect-bypass-btn"
              >
                <FaMoneyBillWave className={`${iconClass} opacity-55`} />
              </button>
            )}
            {/*
              Operator-bypass Skip Payment chrome is available on both the
              Payment page and Checkout. In voucher-only regions the
              customer never reaches Payment, so without this affordance
              on Checkout the operator would have no PIN entry point at
              all to fall back on.
            */}
            {showSkipPayment && (isPaymentPage || isCheckoutPage) && (
              <button
                type="button"
                title={t('controls.skipPayment', 'Skip Payment')}
                onClick={() => setShowSkipPaymentModal(true)}
                className={`text-carbon/75 hover:text-carbon transition-colors ${hitArea}`}
              >
                <FaMoneyBillWave className={`${iconClass} opacity-55`} />
              </button>
            )}
            {!isRootPath && (
              <button
                type="button"
                title={t('controls.exitSession', 'Exit Session')}
                onClick={handleExitClick}
                className={`text-carbon/75 hover:text-carbon transition-colors ${hitArea} ${
                  hideExitButton ? 'opacity-10' : ''
                }`}
              >
                {/* When locked (fullscreen + PIN) nearly hide the X so guests don't mistake it for a
                    back button; brief pulse during the startup grace window keeps it discoverable. */}
                <FaTimes
                  className={`${iconClass} ${
                    fullscreenPinGraceOpacity
                      ? 'opacity-70 animate-pulse'
                      : fullscreenPinIdleOpacity
                        ? 'opacity-5'
                        : ''
                  }`}
                />
              </button>
            )}
          </div>
        </div>
      </div>
      <PinModal
        isOpen={showSubmitPinModal}
        onClose={() => setShowSubmitPinModal(false)}
        onSubmit={handlePinSubmit}
        title={t('controls.exitFullscreen', 'Exit Fullscreen')}
        subtitle={t('controls.enterPin', 'Enter 6-digit PIN to exit fullscreen-mode')}
      />
      <PinModal
        isOpen={showSkipPaymentModal}
        onClose={() => setShowSkipPaymentModal(false)}
        onSubmit={handleSkipPayment}
        title={t('controls.skipPayment', 'Skip Payment')}
        subtitle={t('controls.skipPaymentPin', 'Enter 6-digit PIN to skip payment')}
      />
      
      {/* PIN Modal for accessing Reprint */}
      <PinModal
        isOpen={showReprintPinModal}
        onClose={() => setShowReprintPinModal(false)}
        onSubmit={(pin) => {
          if (pin === password) {
            setShowReprintPinModal(false)
            setShowReprintModal(true)
          } else {
            cogoToast.error(t('controls.wrongPin', 'Oops! Wrong PIN. Please try again.'))
          }
        }}
        title={t('reprint.pinRequired', 'PIN Required')}
        subtitle={t('reprint.enterPinToAccess', 'Enter PIN to access reprint menu')}
      />

      <PinModal
        isOpen={showReprintBypassPinModal}
        onClose={() => setShowReprintBypassPinModal(false)}
        onSubmit={handleReprintBypass}
        title={t('controls.skipPayment', 'Skip Payment')}
        subtitle={t('controls.skipPaymentPin', 'Enter 6-digit PIN to skip payment')}
      />

      <PinModal
        isOpen={showAiBypassPinModal}
        onClose={() => setShowAiBypassPinModal(false)}
        onSubmit={handleAiEffectBypass}
        title={t('controls.skipPayment', 'Skip Payment')}
        subtitle={t('controls.skipPaymentPin', 'Enter 6-digit PIN to skip payment')}
      />

      <PinModal
        isOpen={showExitPinModal}
        onClose={() => setShowExitPinModal(false)}
        onSubmit={handleExitPin}
        title={t('controls.exitSession', 'Exit Session')}
        subtitle={t(
          'controls.exitPaidSessionPin',
          'This session is paid. Enter the 6-digit PIN to end it.'
        )}
      />

      <ReprintModal
        isOpen={showReprintModal}
        onClose={() => setShowReprintModal(false)}
      />

      <PinModal
        isOpen={showDiskPinModal}
        onClose={() => setShowDiskPinModal(false)}
        onSubmit={(pin) => {
          if (pin === password) {
            setShowDiskPinModal(false)
            setShowDiskCleanup(true)
          } else {
            cogoToast.error(t('controls.wrongPin', 'Oops! Wrong PIN. Please try again.'))
          }
        }}
        title={t('diskCleanup.lowTitle', 'Disk almost full')}
        subtitle={t('reprint.pinRequired', 'PIN Required')}
      />
      <DiskCleanupModal
        isOpen={showDiskCleanup}
        onClose={() => setShowDiskCleanup(false)}
        onCleaned={refreshDiskStatus}
      />
      <ConfirmModal
        isOpen={showExitModal}
        onClose={() => setShowExitModal(false)}
        onConfirm={async () => {
          setShowExitModal(false)
          await endSessionAsOperator()
        }}
        title={t('controls.cancelSession', 'Cancel Session')}
        message={t('controls.cancelConfirm', 'Are you sure you want to cancel this session?')}
      />
      <ProcessingModal
        isVisible={isQuitting}
        title={t('controls.quitting', 'Ending Session...')}
        message={t('controls.quittingMessage', 'Please wait while we save your session data')}
        primaryColor={primaryColor}
      />
    </>
  )
}

export default ControlButtons
